import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import '../styles/Cart.css';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    setCheckoutError('');

    try {
      const payload = {
        cartItems: cartItems.map((item) => ({
          id: item.id,
          name: item.name,
          price: item.price,
          priceNum: item.priceNum,
          squareVariationId: item.squareVariationId || `VAR_${item.id}`,
          quantity: item.quantity,
          customizations: item.selectedOptions || {}
        })),
        successUrl: `${window.location.origin}/order-success`,
        cancelUrl: `${window.location.origin}/cart`
      };

      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          'Backend functions are not hosted by plain "npm start" (they run in Cloudflare Pages). Once deployed or linked to Square, this redirects directly to Square.'
        );
      }

      const data = await res.json();

      if (!res.ok || !data.checkoutUrl) {
        throw new Error(data.details || data.error || 'Failed to initiate Square checkout.');
      }

      // Redirect customer to Square Hosted Checkout
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.warn('Square Hosted Checkout notification:', err.message);
      setCheckoutError(err.message || 'Unable to start Square checkout.');
      setIsCheckingOut(false);
    }
  };

  return (
    <section className="cart-page">
      <h1>Shopping Cart</h1>
      
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <p>Your cart is currently empty.</p>
          <Link to="/shop" className="button">Continue Shopping</Link>
        </div>
      ) : (
        <div className="cart-content">
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.cartItemId || item.id} className="cart-item">
                <img src={item.image} alt={item.name} />
                <div className="item-details">
                  <h3>{item.name}</h3>
                  <p className="item-price">{item.price}</p>
                  
                  {/* Selected Options Summary */}
                  {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                    <div className="item-customizations">
                      {item.selectedOptions.color && (
                        <span className="cart-opt-pill">
                          Color: <strong>{item.selectedOptions.color}</strong>
                        </span>
                      )}
                      {item.selectedOptions.member && (
                        <span className="cart-opt-pill">
                          Member: <strong>{item.selectedOptions.member}</strong>
                        </span>
                      )}
                      {item.selectedOptions.theme && (
                        <span className="cart-opt-pill">
                          Theme: <strong>{item.selectedOptions.theme}</strong>
                        </span>
                      )}
                      {item.selectedOptions.officialAlbum && (
                        <span className="cart-opt-pill album-badge">
                          Official Album
                        </span>
                      )}
                      {item.selectedOptions.customNote && (
                        <p className="cart-custom-note">
                          Note: "{item.selectedOptions.customNote}"
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="item-quantity">
                  <button onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}>-</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}>+</button>
                </div>
                <div className="item-remove">
                  <button onClick={() => removeFromCart(item.cartItemId || item.id)} className="remove-btn">Remove</button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>${getCartTotal().toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <hr />
            <div className="summary-row total">
              <span>Total</span>
              <span>${getCartTotal().toFixed(2)}</span>
            </div>
            {checkoutError && (
              <div style={{
                background: '#fee2e2',
                color: '#991b1b',
                padding: '12px 14px',
                borderRadius: '10px',
                fontSize: '0.82rem',
                margin: '12px 0',
                lineHeight: '1.45',
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: '6px' }}>
                  <strong>Checkout Notice:</strong> {checkoutError}
                </div>
                <div>
                  💡 <em>Testing locally?</em>{' '}
                  <Link to="/order-success?orderId=DEMO-TEST-12345" style={{ color: '#b91c1c', fontWeight: 600, textDecoration: 'underline' }}>
                    Click here to preview the Order Confirmation page
                  </Link>
                </div>
              </div>
            )}
            <button 
              type="button" 
              onClick={handleCheckout} 
              disabled={isCheckingOut} 
              className="button checkout-btn"
              style={{ width: '100%', cursor: isCheckingOut ? 'wait' : 'pointer', opacity: isCheckingOut ? 0.75 : 1 }}
            >
              {isCheckingOut ? 'Connecting to Square Checkout...' : 'Proceed to Checkout'}
            </button>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '14px', textAlign: 'center', lineHeight: '1.4' }}>
              All sales are final. Stamped mail risk assumed by buyer. International taxes/customs not included. <Link to="/shipping-policy" style={{ color: '#111827', textDecoration: 'underline' }}>View Policies</Link>
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Cart;
